import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { EmailDraftConfig, ScreenBox } from "../scenario/types";
import { audio } from "../lib/audio";
import { SkipButton } from "../ui/SkipButton";

/**
 * "The Influencer" arc — the spear-phishing email draft, glued to the same
 * hacker-room `screenQuad` as `MitmReveal`/`HackerZoom`. A short typed-out
 * reveal of a highly targeted message referencing the confidential detail
 * just spotted in the photo, ending on a "Sent" sting.
 */
export function EmailDraft({
  config,
  onDone,
  screenBox,
}: {
  config: EmailDraftConfig;
  onDone: () => void;
  screenBox?: ScreenBox | null;
}) {
  const [shownLines, setShownLines] = useState(0);
  const [sent, setSent] = useState(false);
  const [ready, setReady] = useState(false);
  const started = useRef(false);
  const skipped = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    let cancelled = false;
    (async () => {
      if (config.narrationLine) {
        void audio.speak(config.narrationLine, {
          voice: config.voiceId ?? "onyx",
          lang: config.language ?? "en-US",
          instructions: "Quiet, satisfied, typing as he talks — an attacker composing a targeted message.",
        });
      }
      const lines = config.bodyLines ?? [];
      for (let i = 0; i < lines.length; i++) {
        if (cancelled || skipped.current || !mounted.current) return;
        setShownLines(i + 1);
        audio.click();
        await new Promise((r) => window.setTimeout(r, Math.min(1400, 500 + lines[i].length * 18)));
      }
      if (cancelled || skipped.current || !mounted.current) return;
      await new Promise((r) => window.setTimeout(r, 500));
      if (cancelled || skipped.current || !mounted.current) return;
      audio.dataBlip();
      setSent(true);
      setReady(true);
    })();
    return () => {
      cancelled = true;
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    if (skipped.current) return;
    skipped.current = true;
    audio.cancelSpeech();
    setShownLines((config.bodyLines ?? []).length);
    setSent(true);
    setReady(true);
  };

  const glued = !!(screenBox && screenBox.visible);
  const wrapStyle: CSSProperties | undefined = glued
    ? {
        left: `${screenBox!.leftPct}%`,
        top: `${screenBox!.topPct}%`,
        width: `${screenBox!.widthPct}%`,
        height: `${screenBox!.heightPct}%`,
        right: "auto",
        bottom: "auto",
        transform: "none",
        alignItems: "stretch",
        justifyContent: "stretch",
      }
    : undefined;

  return (
    <div className={`mitm-wrap edraft-wrap ${glued ? "mitm-wrap--glued" : ""}`} style={wrapStyle} role="alertdialog" aria-label="Email draft">
      <div className="mitm-card edraft-card">
        <div className="mitm-head">
          <span className="mitm-dot" /> {sent ? "MESSAGE SENT" : "COMPOSING"}
        </div>

        <div className="edraft-mail">
          <div className="edraft-row">
            <span className="edraft-label">To</span>
            <span className="edraft-val">
              {config.toName}
              {config.toEmail ? ` <${config.toEmail}>` : ""}
            </span>
          </div>
          <div className="edraft-row">
            <span className="edraft-label">From</span>
            <span className="edraft-val">{config.fromLabel ?? "Tom Reyes <t.reyes@acmecorp.com>"}</span>
          </div>
          <div className="edraft-row">
            <span className="edraft-label">Subject</span>
            <span className="edraft-val edraft-subject">{config.subject}</span>
          </div>
          <div className="edraft-body">
            {(config.bodyLines ?? []).slice(0, shownLines).map((l, i) => (
              <div key={i} className="edraft-body-line">
                {l}
              </div>
            ))}
            {!sent && shownLines < (config.bodyLines ?? []).length && <span className="edraft-cursor" aria-hidden>{"\u258A"}</span>}
          </div>
          {sent && <div className="edraft-sent-badge">{config.sentLabel ?? "Sent"}</div>}
        </div>

        <div className="cq-continue-group mitm-continue-group">
          <button type="button" className="btn mitm-continue" disabled={!ready} onClick={onDone}>
            {ready ? config.continueLabel ?? "Continue" : config.sendingLabel ?? "\u2026"}
          </button>
          {!ready && <SkipButton onClick={skip} label="Skip ahead" />}
        </div>
      </div>
    </div>
  );
}
